import {
  AMPLIFY_ELEMENTARY_STUDENT_APPS_ASSET,
  AMPLIFY_HUB_ASSET,
  AMPLIFY_MCLASS_ASSET,
  AMPLIFY_OLD_MCLASS_ASSET,
  BOOST_LECTURA_ASSET_BK,
  BOOST_READING_ASSETS_BKS,
  CLOSE_READING,
  CONNECT_THE_BLOCKS_ASSET,
  FLUENCY_PRACTICE_ASSET,
  FORMULA_WON_ASSET,
  MCLASS_LECTURA_ASSET_BK,
  MCLASS_READING_ASSET_BK,
  NUMBER_JUMPER_ASSET,
  POLYPAD_ASSET,
  SCIENCE_APPS_ASSET,
  TWELVE_A_DOZEN_ASSET,
  TX_ASSET_BK,
} from 'src/constants/assets.constant';
import { MS_CORE_PRODUCTS_ENTITLEMENTS_KEYS } from 'src/constants/ms-products.constants';
import { Asset } from 'src/interfaces/assets.types';
import { MSProductEntitlement } from 'src/interfaces/ms-product.types';
import { hasAsset, hasAssets } from 'src/utils/assets.utils';

export const hasBoostReading = (userAssets?: Asset[]) =>
  hasAssets(userAssets, BOOST_READING_ASSETS_BKS);

export const hasBoostLectura = (userAssets?: Asset[]) =>
  hasAsset(BOOST_LECTURA_ASSET_BK, userAssets);

export const hasCloseReading = (userAssets?: Asset[]) =>
  hasAssets(userAssets, CLOSE_READING);

export const hasTxAsset = (userAssets: Asset[] | undefined) =>
  hasAsset(TX_ASSET_BK, userAssets);

export const hasMClassEntitlement = (userAssets: Asset[] | undefined) =>
  hasAsset(AMPLIFY_MCLASS_ASSET, userAssets);

export const hasOldMClassEntitlement = (userAssets: Asset[] | undefined) =>
  hasAsset(AMPLIFY_OLD_MCLASS_ASSET, userAssets);

export const hasMClassReadingEntitlement = (userAssets: Asset[] | undefined) =>
  hasAsset(MCLASS_READING_ASSET_BK, userAssets);

export const hasMClassLecturaEntitlement = (userAssets: Asset[] | undefined) =>
  hasAsset(MCLASS_LECTURA_ASSET_BK, userAssets);

export const hasHubEntitlement = (userAssets: Asset[] | undefined) =>
  hasAsset(AMPLIFY_HUB_ASSET, userAssets);

export const hasElementaryStudentAppsEntitlement = (
  userAssets: Asset[] | undefined,
) => hasAsset(AMPLIFY_ELEMENTARY_STUDENT_APPS_ASSET, userAssets);

export const hasScienceAppsEntitlement = (userAssets: Asset[] | undefined) =>
  hasAsset(SCIENCE_APPS_ASSET, userAssets);

export const hasFluencyPracticeEntitlement = (
  userAssets: Asset[] | undefined,
  isSelfSignupUser = false,
) => isSelfSignupUser || hasAsset(FLUENCY_PRACTICE_ASSET, userAssets);

export const hasPolypadEntitlement = (
  userAssets: Asset[] | undefined,
  isSelfSignupUser = false,
) => isSelfSignupUser || hasAsset(POLYPAD_ASSET, userAssets);

export const hasFormulaWonEntitlement = (userAssets: Asset[] | undefined) =>
  hasAsset(FORMULA_WON_ASSET, userAssets);

export const hasTwelveADozenEntitlement = (userAssets: Asset[] | undefined) =>
  hasAsset(TWELVE_A_DOZEN_ASSET, userAssets);

export const hasNumberJumperEntitlement = (userAssets: Asset[] | undefined) =>
  hasAsset(NUMBER_JUMPER_ASSET, userAssets);

export const hasConnectTheBlocksEntitlement = (
  userAssets: Asset[] | undefined,
) => hasAsset(CONNECT_THE_BLOCKS_ASSET, userAssets);

export const getHardcodedProductEntitlement = (
  userAssets: Asset[] | undefined,
  isSelfSignupUser = false,
): MSProductEntitlement => {
  // Cache repeated function calls
  const hasBoostReadingAssets = hasBoostReading(userAssets);
  const hasBoostLecturaAssets = hasBoostLectura(userAssets);
  const hasMClassReadingAssets = hasMClassReadingEntitlement(userAssets);
  const hasMClassLecturaAssets = hasMClassLecturaEntitlement(userAssets);
  const hasNewMClassAssets = hasMClassReadingAssets || hasMClassLecturaAssets;

  return {
    hasBoostReading: hasBoostReadingAssets && !hasBoostLecturaAssets,
    hasBoostLectura: hasBoostLecturaAssets && !hasBoostReadingAssets,
    hasBoostBiliteracy: hasBoostReadingAssets && hasBoostLecturaAssets,
    hasCloseReading: hasCloseReading(userAssets),
    hasMClass: hasOldMClassEntitlement(userAssets) && !hasNewMClassAssets,
    // only one card for mCLASS will appear
    hasMClassReading: hasMClassReadingAssets && !hasMClassLecturaAssets,
    hasMClassLectura: hasMClassLecturaAssets && !hasMClassReadingAssets,
    hasMClassBiliteracy: hasMClassLecturaAssets && hasMClassReadingAssets,
    hasHub: hasHubEntitlement(userAssets),
    hasScienceApps: hasScienceAppsEntitlement(userAssets),
    hasFluencyPractice: hasFluencyPracticeEntitlement(
      userAssets,
      isSelfSignupUser,
    ),
    hasPolypad: hasPolypadEntitlement(userAssets, isSelfSignupUser),
    hasFormulaWon: hasFormulaWonEntitlement(userAssets),
    hasTwelveADozen: hasTwelveADozenEntitlement(userAssets),
    hasNumberJumper: hasNumberJumperEntitlement(userAssets),
    hasConnectTheBlocks: hasConnectTheBlocksEntitlement(userAssets),
  };
};

export const checkHasCoreProductsEntitlements = (
  msProductEntitlement: MSProductEntitlement,
): boolean => {
  return MS_CORE_PRODUCTS_ENTITLEMENTS_KEYS.some(
    (entitlement) => msProductEntitlement[entitlement],
  );
};
